import { CardGrid } from '../../components/CardGrid';
import { ContentArea } from '../../components/ContentArea';
import { PageHeading } from '../../components/PageHeading';
import { PageSection } from '../../components/Section';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { SkeletonCard } from '../../components/SkeletonCard';
import { StickyFooter } from '../../components/StickyFooter';
import { ActionBar } from '../../composites/ActionBar';
import { ExtrasCard } from '../../composites/ExtrasCard';
import { ExtrasModals, useExtrasSelection } from '../../composites/ExtrasModals';
import { GridArea, HeadingArea, TopBarSlot } from './ExtrasTemplate.styles';
import type { ExtrasTemplateProps } from './ExtrasTemplate.types';

export function ExtrasTemplate({
  items,
  imageAspectRatio = '1 / 1',
  imageMobileAspectRatio = '3 / 2',
  totalAmount,
  originalAmount,
  currencyCode,
  locale,
  guests,
  currentStep,
  totalSteps,
  onBack,
  onAdd,
  onRemove,
  onConfirmGuestSelection,
  onConfirmSelection,
  onCtaClick,
  ctaDisabled,
  isLoading = false,
  heading,
  cardVariant,
  hideProgress = false,
  labels,
}: ExtrasTemplateProps) {
  const {
    title,
    subtitle,
    backAriaLabel,
    add,
    remove,
    quantity,
    editSelections,
    noImage,
    footerTitle,
    footerTotal,
    ctaLabel,
    progressLabel,
    progressValueText,
    fromPrice,
    perGuestSuffix,
  } = labels;

  const showProgress = !hideProgress && totalSteps != null && totalSteps > 0;

  const selection = useExtrasSelection({
    items,
    guests,
    onAdd,
    onConfirmGuestSelection,
    onConfirmSelection,
  });

  const topBar =
    onBack || showProgress ? (
      <TopBarSlot>
        <ActionBar
          onBack={onBack}
          backAriaLabel={backAriaLabel}
          currentStep={currentStep}
          totalSteps={showProgress ? totalSteps : undefined}
          progressLabel={progressLabel}
          progressValueText={progressValueText}
        />
      </TopBarSlot>
    ) : null;

  if (isLoading) {
    return (
      <>
        {topBar}

        <PageSection aria-busy="true">
          <HeadingArea>
            <SkeletonBlock variant="heading" />
          </HeadingArea>

          <ContentArea>
            <GridArea>
              <CardGrid $columns={3} $tabletColumns={2} $flushBottom>
                {Array.from({ length: 6 }).map((_, index) => (
                  <SkeletonCard key={index} variant="grid" />
                ))}
              </CardGrid>
            </GridArea>
          </ContentArea>
        </PageSection>

        <StickyFooter
          title={footerTitle}
          totalLabel={footerTotal}
          totalAmount={totalAmount}
          originalAmount={originalAmount}
          currencyCode={currencyCode}
          locale={locale}
          ctaLabel={ctaLabel}
          onCtaClick={onCtaClick}
          ctaDisabled
        />
      </>
    );
  }

  return (
    <>
      {topBar}

      <PageSection>
        <HeadingArea>
          <PageHeading
            title={title}
            subtitle={subtitle}
            eyebrow={heading?.eyebrow}
            subtitlePlacement={heading?.subtitlePlacement}
            centered
            flush
          />
        </HeadingArea>

        <ContentArea>
          <GridArea>
            <CardGrid $columns={3} $tabletColumns={2} $flushBottom>
              {items.map((item) => (
                <ExtrasCard
                  key={item.id}
                  image={item.image}
                  imageAspectRatio={imageAspectRatio}
                  imageMobileAspectRatio={imageMobileAspectRatio}
                  title={item.title}
                  description={item.description}
                  variant={cardVariant}
                  price={{
                    ...item.price,
                    locale,
                    label: fromPrice,
                    suffix: item.perPerson ? perGuestSuffix : undefined,
                  }}
                  selected={item.selected}
                  quantity={item.quantity}
                  onAdd={() => selection.handleAdd(item)}
                  onRemove={() => onRemove(item.id)}
                  onEditSelections={
                    selection.canEditSelections(item) && item.selected
                      ? () => selection.handleEditSelections(item)
                      : undefined
                  }
                  labels={{ add, remove, quantity, editSelections, noImage }}
                />
              ))}
            </CardGrid>
          </GridArea>
        </ContentArea>
      </PageSection>
      <StickyFooter
        title={footerTitle}
        totalLabel={footerTotal}
        totalAmount={totalAmount}
        originalAmount={originalAmount}
        currencyCode={currencyCode}
        locale={locale}
        ctaLabel={ctaLabel}
        onCtaClick={onCtaClick}
        ctaDisabled={ctaDisabled}
      />
      <ExtrasModals
        selection={selection}
        items={items}
        guests={guests}
        currencyCode={currencyCode}
        locale={locale}
        labels={labels}
      />
    </>
  );
}
